<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import IconAdd from '~icons/material-symbols/add'
import IconDocument from '~icons/material-symbols/description-outline'
import IconProgressActivity from '~icons/material-symbols/progress-activity'
import DocumentPreviewModal from '~/components/input/FileUpload/DocumentPreviewModal.vue'

type ExistingFile = Record<string, unknown>
type FileUploadValue = File | ExistingFile | null | undefined

const props = withDefaults(defineProps<{
  modelValue?: FileUploadValue
  accept?: string
  maxSizeMb?: number
  loading?: boolean
  error?: string
  fileName?: string
  helperText?: string
  disabled?: boolean
  skeleton?: boolean
  viewUrl?: string
  downloadUrl?: string
  id?: string
  labelledBy?: string
  previewMimeType?: string
  previewId?: string
}>(), {
  accept: '.pdf,.jpeg,.jpg,.png',
  maxSizeMb: 10,
  loading: false,
  disabled: false,
  skeleton: false,
})

const emit = defineEmits<{
  'update:modelValue': [file: File | null]
}>()

const { t } = useI18n()

const fileInput = ref<HTMLInputElement>()
const isDragging = ref(false)
const internalError = ref<string | undefined>()
const isPreviewOpen = ref(false)

const currentFileName = computed<string | null>(() => {
  if (!props.modelValue)
    return null
  if (props.modelValue instanceof File)
    return (props.modelValue as File).name
  return props.fileName ?? t('fileUpload.existingFile')
})

const displayError = computed(() => internalError.value || props.error)

type UploadState = 'loading' | 'success' | 'error' | 'empty'

const UPLOAD_STATE_PRIORITY: Exclude<UploadState, 'empty'>[] = ['loading', 'success', 'error']

const uploadState = computed<UploadState>(() => {
  const flags: Record<(typeof UPLOAD_STATE_PRIORITY)[number], boolean> = {
    loading: props.loading,
    success: !!currentFileName.value,
    error: !!displayError.value,
  }

  const match = UPLOAD_STATE_PRIORITY.find(key => flags[key])
  return match ?? 'empty'
})

const acceptedExtensions = computed(() =>
  props.accept.split(',').map(ext => ext.trim().toLowerCase().replace(/^\./, '')),
)

const helperTextFallback = computed(() => {
  const exts = acceptedExtensions.value.map(e => e.toUpperCase()).join(', ')
  return t('fileUpload.helperText', { exts, maxSizeMb: props.maxSizeMb })
})

function openFilePicker() {
  if (props.loading || props.disabled)
    return
  fileInput.value?.click()
}

function replaceFile() {
  if (props.disabled)
    return
  emit('update:modelValue', null)
  internalError.value = undefined
  fileInput.value?.click()
}

function openPreview() {
  if (!props.viewUrl)
    return
  isPreviewOpen.value = true
}

function downloadFile() {
  if (!props.downloadUrl)
    return
  window.open(props.downloadUrl, '_blank', 'noopener')
}

function validateFile(file: File): string | null {
  const maxBytes = props.maxSizeMb * 1024 * 1024
  if (file.size > maxBytes)
    return t('fileUpload.errors.maxSize', { maxSizeMb: props.maxSizeMb })

  const fileExt = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (!acceptedExtensions.value.includes(fileExt))
    return t('fileUpload.errors.invalidFormat', { formats: acceptedExtensions.value.map(e => `.${e}`).join(', ') })

  return null
}

function handleFile(file: File) {
  internalError.value = undefined
  const err = validateFile(file)
  if (err) {
    internalError.value = err
    return
  }
  emit('update:modelValue', file)
}

function onFileChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file)
    handleFile(file)
  ;(event.target as HTMLInputElement).value = ''
}

function onDragOver(event: DragEvent) {
  if (props.disabled || props.loading)
    return
  event.preventDefault()
  isDragging.value = true
}

function onDrop(event: DragEvent) {
  isDragging.value = false
  if (props.disabled || props.loading)
    return
  const file = event.dataTransfer?.files?.[0]
  if (file)
    handleFile(file)
}
</script>

<template>
  <div class="file-upload-wrapper">
    <div
      v-if="skeleton"
      class="file-upload-area state-skeleton"
    >
      <div class="skeleton-icon skeleton-pulse" />
      <div class="skeleton-label skeleton-pulse" />
      <div class="skeleton-helper skeleton-pulse" />
    </div>

    <div
      v-else
      :id="id ? `file-upload-${id}` : undefined"
      :data-testid="id ? `file-upload-${id}` : undefined"
      :aria-labelledby="labelledBy"
      class="file-upload-area"
      :class="[`state-${uploadState}`, {
        'is-dragging': isDragging,
        'is-disabled': disabled,
        'is-invalid': uploadState === 'success' && !!displayError,
      }]"
      role="button"
      :tabindex="uploadState === 'success' || disabled ? -1 : 0"
      @click="!disabled && uploadState !== 'success' ? openFilePicker() : undefined"
      @keydown.enter="!disabled && uploadState !== 'success' ? openFilePicker() : undefined"
      @keydown.space.self.prevent="!disabled && uploadState !== 'success' ? openFilePicker() : undefined"
      @dragover="onDragOver"
      @dragleave="isDragging = false"
      @drop.prevent="onDrop"
    >
      <template v-if="uploadState === 'loading'">
        <IconProgressActivity class="state-icon icon-spin text-neutral-low-dark" />
        <span class="state-label-loading">{{ t('fileUpload.loading') }}</span>
      </template>

      <template v-else-if="uploadState === 'success'">
        <div class="filled-icon-circle">
          <IconDocument class="filled-icon" />
        </div>
        <span class="state-filename">{{ currentFileName }}</span>
        <div class="file-actions">
          <button
            v-if="viewUrl"
            type="button"
            class="action-btn view-btn"
            @click.stop="openPreview"
          >
            {{ t('fileUpload.view') }}
          </button>
          <button
            v-if="downloadUrl"
            :id="id ? `file-download-${id}` : undefined"
            type="button"
            class="action-btn download-btn"
            @click.stop="downloadFile"
          >
            {{ t('fileUpload.download') }}
          </button>
          <button
            v-if="!disabled"
            type="button"
            class="action-btn replace-btn"
            @click.stop="replaceFile"
          >
            {{ t('fileUpload.replace') }}
          </button>
        </div>
      </template>

      <template v-else>
        <div class="empty-icon-circle">
          <IconAdd class="state-icon" />
        </div>
        <strong class="state-label">{{ t('fileUpload.emptyLabel') }}</strong>
        <span class="state-helper">{{ helperText ?? helperTextFallback }}</span>
      </template>
    </div>

    <input
      :id="id ? `input-file-upload-${id}` : undefined"
      ref="fileInput"
      :data-testid="id ? `input-file-upload-${id}` : undefined"
      :name="id"
      type="file"
      :accept="accept"
      class="hidden"
      tabindex="-1"
      @change="onFileChange"
    >

    <span v-if="displayError" class="upload-error-msg">
      {{ displayError }}
    </span>

    <DocumentPreviewModal
      v-if="viewUrl"
      :is-open="isPreviewOpen"
      :title="currentFileName ?? ''"
      :url="viewUrl"
      :file-path="currentFileName ?? undefined"
      :mime-type="previewMimeType"
      :modal-id="previewId"
      @close="isPreviewOpen = false"
    />
  </div>
</template>

<style scoped lang="scss">
@keyframes skeleton-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.skeleton-pulse {
  background: linear-gradient(
    90deg,
    #e8e8e8 25%,
    #f5f5f5 50%,
    #e8e8e8 75%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

.file-upload-wrapper {
  @apply flex flex-col gap-1 w-full;

  .file-upload-area {
    @apply relative flex flex-col items-center justify-center gap-1;
    @apply w-full border-2 border-dashed rounded-xl;
    @apply transition-colors duration-200 cursor-pointer;
    @apply p-8 text-center min-h-[160px];

    &.state-skeleton {
      @apply border-neutral-high-medium bg-neutral-high-pure cursor-default;

      .skeleton-icon {
        @apply w-10 h-10 rounded-full mb-3;
      }

      .skeleton-label {
        @apply h-5 w-40 rounded-lg;
      }

      .skeleton-helper {
        @apply h-3 w-56 rounded-lg mt-1;
      }
    }

    &.state-empty {
      @apply border-neutral-high-medium bg-neutral-high-pure;

      &:hover,
      &.is-dragging {
        @apply border-neutral-low-dark bg-neutral-high-pure;
        border-style: solid;
        background-color: rgba(200, 200, 200, 0.16);

        .empty-icon-circle {
          @apply bg-neutral-high-medium;
        }
      }
    }

    &.state-error {
      @apply border-feedback-negative-pure;
      background-color: rgba(220, 38, 38, 0.03);
    }

    &.state-loading {
      @apply border-neutral-high-medium bg-neutral-high-pure cursor-default;
    }

    &.state-success {
      @apply border-neutral-high-medium bg-neutral-high-pure cursor-default;
      border-style: solid;
    }

    &.is-disabled {
      @apply cursor-default;
      background-color: rgba(200, 200, 200, 0.16);
      border: 1px solid #222;

      .empty-icon-circle {
        @apply bg-neutral-high-light;

        .state-icon {
          @apply text-neutral-high-dark;
        }
      }

      .state-label {
        @apply text-neutral-high-dark;
      }

      .state-helper {
        color: #a3a3a3;
      }

      &:not(.state-success) {
        @apply pointer-events-none;
      }
    }

    &.state-success.is-invalid {
      @apply border-feedback-negative-pure;
    }

    .empty-icon-circle {
      @apply flex items-center justify-center mb-3;
      @apply w-10 h-10 rounded-full bg-neutral-high-light;

      .state-icon {
        @apply w-5 h-5 text-neutral-low-light;
      }
    }

    .filled-icon-circle {
      @apply flex items-center justify-center mb-3;
      @apply w-10 h-10 rounded-full;
      @apply border-2 border-solid border-neutral-low-dark;

      .filled-icon {
        @apply w-5 h-5 text-neutral-low-dark;
      }
    }

    .state-icon {
      @apply w-10 h-10;

      &.icon-spin {
        animation: spin 1s linear infinite;
      }
    }

    .state-label-loading {
      @apply fonts-body-x-large-bold text-neutral-low-dark;
    }

    .state-label {
      @apply fonts-body-large-bold text-neutral-low-dark;
    }

    .state-helper {
      @apply fonts-body-small-regular text-neutral-low-medium;
    }

    .state-filename {
      @apply fonts-body-large-bold text-neutral-low-dark;
      @apply max-w-full break-all;
    }

    .file-actions {
      @apply flex items-center justify-center flex-wrap gap-4 mt-1;
    }

    .action-btn {
      @apply fonts-body-medium-bold text-neutral-low-dark underline;
      @apply bg-transparent border-0 cursor-pointer p-0;
      @apply hover:opacity-80 transition-opacity;
      font-size: 12px;
    }
  }

  .upload-error-msg {
    @apply block fonts-body-small-regular text-feedback-negative-pure;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
